import React, { FC, ReactElement, useState, useRef, useEffect } from 'react';
import { StringHelper } from '../../../../common/helpers/string-helper';
import { StringValueCallback } from '../../../../types/Callbacks';

interface IDropDownProps {
	id: string;
	dataCy?: string;
	inErrorState?: boolean;
	header?: string;
	includeDefaultItem?: boolean;
	dataSource: string[];
	title?: string;
	onSelect?: StringValueCallback;
}

const DropDown: FC<IDropDownProps> = ({
	id,
	dataCy,
	inErrorState = false,
	header,
	includeDefaultItem = false,
	dataSource,
	title = 'Select',
	onSelect,
}) => {
	const [selectedTitle, setSelectedTitle] = useState<string>(StringHelper.Empty);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleSelect = (value: string, displayText: string) => {
		if (onSelect) {
			onSelect(value);
		} else {
			setSelectedTitle(displayText);
		}
		setIsOpen(false);
	};

	const handleClear = () => {
		setSelectedTitle(StringHelper.Empty);
		if (onSelect) {
			onSelect(StringHelper.Empty);
		}
		setIsOpen(false);
	};

	const buttonClasses = `
		w-full px-4 h-9 text-left bg-gray-50 border rounded-full cursor-pointer 
		transition-all duration-150 ease-in-out flex justify-between items-center
		hover:bg-gray-100 hover:border-gray-400
		focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
		${inErrorState ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
	`.trim().replace(/\s+/g, ' ');

	return (
		<div ref={dropdownRef} className="relative inline-block w-full">
			<button
				data-cy={dataCy}
				aria-required={true}
				id={id}
				type="button"
				className={buttonClasses}
				onClick={() => setIsOpen(!isOpen)}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
			>
				<span className="flex-1 truncate text-gray-700">
					{StringHelper.SnakeCaseToFriendlyString(title || selectedTitle)}
				</span>
				<span className={`ml-2 text-xs text-gray-500 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}>
					▼
				</span>
			</button>

			{isOpen && (
				<div 
					className="absolute top-full left-0 right-0 mt-0.5 bg-white border border-tertiary-background rounded-md shadow-elev-1-card z-50 max-h-48 overflow-y-auto"
					role="listbox"
				>
					{header && (
						<>
							<div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase bg-transparent">
								{title}
							</div>
							<hr className="my-2 border-t border-gray-200" />
						</>
					)}
					
					{includeDefaultItem && (
						<button
							type="button"
							className="w-full px-4 py-2 text-left text-gray-800 hover:bg-gray-50 focus:outline-none focus:bg-gray-100 active:bg-blue-500 active:text-white transition-colors duration-150"
							onClick={handleClear}
							role="option"
						>
							Clear
						</button>
					)}
					
					{dataSource.map((value, index) => (
						<button
							key={index}
							type="button"
							className="w-full px-4 py-2 text-left text-gray-800 hover:bg-gray-50 focus:outline-none focus:bg-gray-100 active:bg-blue-500 active:text-white transition-colors duration-150"
							onClick={() => handleSelect(value, StringHelper.SnakeCaseToFriendlyString(value))}
							role="option"
						>
							{StringHelper.SnakeCaseToFriendlyString(value)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default DropDown;
// import styles from './DropDown.module.scss';
// import React, { FC, ReactElement, useState } from 'react';
// import { Dropdown, DropdownButton } from 'react-bootstrap';
// import { StringHelper } from '../../../../common/helpers/string-helper';
// import { StringValueCallback } from '../../../../types/Callbacks';

// interface IDropDownProps {
// 	id: string;
// 	dataCy?: string;
// 	inErrorState?: boolean;
// 	header?: string;
// 	includeDefaultItem?: boolean;
// 	dataSource: string[];
// 	title?: string;
// 	onSelect?: StringValueCallback;
// }

// const DropDown: FC<IDropDownProps> = ({
// 	id,
// 	dataCy,
// 	inErrorState = false,
// 	header,
// 	includeDefaultItem = false,
// 	dataSource,
// 	title = 'Select',
// 	onSelect,
// }) => {
// 	const [selectedTitle, setSelectedTitle] = useState<string>(StringHelper.Empty);

// 	return (
// 		<DropdownButton
// 			data-cy={dataCy}
// 			aria-required={true}
// 			id={id}
// 			variant={'secondary'}
// 			className={`${styles.drop_down}${inErrorState ? ` ${styles.drop_down__error}` : StringHelper.Empty}`}
// 			title={StringHelper.SnakeCaseToFriendlyString(title || selectedTitle)}
// 			onSelect={(_, event: any) => {
// 				if (onSelect) onSelect(event.target.id);
// 				else setSelectedTitle(event.target.innerHTML);
// 			}}
// 		>
// 			{header && <Dropdown.Header>{title}</Dropdown.Header>}
// 			{header && <Dropdown.Divider />}
// 			{includeDefaultItem && <Dropdown.Item>Clear</Dropdown.Item>}
// 			{dataSource.map((value, index) => {
// 				return (
// 					<Dropdown.Item id={value} key={index} as={'button'}>
// 						{StringHelper.SnakeCaseToFriendlyString(value)}
// 					</Dropdown.Item>
// 				);
// 			})}
// 		</DropdownButton>
// 	);
// };

// export default DropDown;
